// Single source of truth: Country name to ISO 3166-1 alpha-2 code mapping
export const COUNTRY_CODES = {
  'United States': 'us',
  'Canada': 'ca',
  'United Kingdom': 'gb',
  'Australia': 'au',
  'France': 'fr',
  'Germany': 'de',
  'Italy': 'it',
  'Spain': 'es',
  'Netherlands': 'nl',
  'Belgium': 'be',
  'Switzerland': 'ch',
  'Austria': 'at',
  'Sweden': 'se',
  'Norway': 'no',
  'Denmark': 'dk',
  'Finland': 'fi',
  'Ireland': 'ie',
  'Portugal': 'pt',
  'Greece': 'gr',
  'Poland': 'pl',
  'Japan': 'jp',
  'China': 'cn',
  'South Korea': 'kr',
  'Singapore': 'sg',
  'New Zealand': 'nz',
  'Mexico': 'mx',
  'India': 'in',
  'Brazil': 'br',
  'Argentina': 'ar',
  'Chile': 'cl',
  'Colombia': 'co',
  'Peru': 'pe',
  'South Africa': 'za',
};

// Derive country names array from codes (used in dropdown)
export const COUNTRIES = Object.keys(COUNTRY_CODES);

// Get array of country codes for Google Places API restrictions
export const getCountryCodes = () => Object.values(COUNTRY_CODES);

export const addSpaceBeforeEveryCapitalLetter = (str) => {
  const addSpace = str.replace(/(?=[A-Z])/g, " ");
  const capitalizeFirstChar =
    addSpace.charAt(0).toUpperCase() + addSpace.slice(1);
  return capitalizeFirstChar;
};

export const parseAddressString = (addressString) => {
  if (!addressString) return null;

  // Split by comma
  const parts = addressString.split(',').map(s => s.trim());

  // Check if last segment is a country
  let country = 'United States';  // Default to US for legacy addresses
  let addressParts = parts;

  if (parts.length > 1) {
    const lastPart = parts[parts.length - 1];
    // Check if last segment matches a known country
    if (COUNTRIES.includes(lastPart)) {
      country = lastPart;
      addressParts = parts.slice(0, -1);  // Remove country from address parts
    }
  }

  // Now parse the address parts (without country)
  if (addressParts.length < 2) {
    // Malformed address, return what we can
    return {
      address: addressParts[0] || '',
      city: '',
      stateProvince: '',
      postalCode: '',
      country: country
    };
  }

  const address = addressParts[0];
  const localityPart = addressParts.slice(1).join(', ');  // Rejoin remaining parts

  // Split locality by spaces to extract city/state/postal
  const localitySegments = localityPart.trim().split(/\s+/).filter(Boolean);

  if (localitySegments.length === 0) {
    return {
      address,
      city: '',
      stateProvince: '',
      postalCode: '',
      country
    };
  }

  // Extract postal code (last segment, or last 2 if it's a space-separated postal like "SW1A 2AA")
  let postalCode = '';
  let remainingSegments = [...localitySegments];

  // Check if last segment looks like postal code
  const lastSeg = remainingSegments[remainingSegments.length - 1];
  if (lastSeg && lastSeg.match(/^[A-Z0-9]+$/i)) {
    postalCode = remainingSegments.pop();

    // Check if second-to-last is also part of postal (for UK style "SW1A 2AA")
    // UK postcodes have mixed letters and numbers (e.g., "SW1A"), unlike US state codes (e.g., "NV")
    const secondLast = remainingSegments[remainingSegments.length - 1];
    if (secondLast && secondLast.match(/^[A-Z0-9]+$/i) && secondLast.match(/[A-Z]/i) && secondLast.match(/[0-9]/) && secondLast.length <= 4) {
      postalCode = remainingSegments.pop() + ' ' + postalCode;
    }
  }

  // Extract state/province (last remaining segment if it looks like a state code)
  let stateProvince = '';
  if (remainingSegments.length > 1) {
    const lastRemaining = remainingSegments[remainingSegments.length - 1];
    // If it's 2-3 uppercase chars, likely a state code (CA, ON, NSW, etc.)
    if (lastRemaining && lastRemaining.match(/^[A-Z]{2,3}$/)) {
      stateProvince = remainingSegments.pop();
    }
  }

  // Everything else is the city
  const city = remainingSegments.join(' ');

  return {
    address,
    city,
    stateProvince,
    postalCode,
    country
  };
};

export const stringifyAddressObject = (addressObject) => {
  if (!addressObject) return '';
  const { address, city, stateProvince, postalCode, country } = addressObject;

  // Build the locality part (city state postal or city postal)
  let localityPart;
  if (stateProvince && stateProvince.trim()) {
    localityPart = `${city} ${stateProvince} ${postalCode}`;
  } else {
    localityPart = `${city} ${postalCode}`;
  }

  // For US addresses or if no country specified, use legacy format (no country suffix)
  if (!country || country === 'United States') {
    return `${address}, ${localityPart}`;
  }

  // For non-US addresses, append country
  return `${address}, ${localityPart}, ${country}`;
};
